/**
 * GradientSelect Component
 *
 * Modern select dropdown with gradient focus effects and icon support
 * Enhanced with size variants, accessibility, and better TypeScript support
 */
import { useState, forwardRef } from 'react';

export interface GradientSelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: React.ReactNode;
  icon?: React.ReactNode;
  error?: string;
  wrapperClassName?: string;
  size?: 'sm' | 'default' | 'lg';
  helperText?: string;
  children: React.ReactNode;
}

export const GradientSelect = forwardRef<HTMLSelectElement, GradientSelectProps>(
  (
    {
      label,
      icon,
      error,
      wrapperClassName = '',
      size = 'default',
      helperText,
      disabled = false,
      children,
      onChange,
      className,
      id,
      ...selectProps
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      e.stopPropagation();
      onChange?.(e);
    };

    // Generate ID if not provided
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

    // Size variants
    const sizeClasses = {
      sm: 'h-8 text-sm',
      default: 'h-10 text-base',
      lg: 'h-12 text-lg',
    };

    const paddingClasses = {
      sm: icon ? 'pl-8 pr-8' : 'pl-3 pr-8',
      default: icon ? 'pl-10 pr-8' : 'pl-3 pr-8',
      lg: icon ? 'pl-12 pr-10' : 'pl-4 pr-10',
    };

    const iconSizeClasses = {
      sm: 'w-3 h-3',
      default: 'w-4 h-4',
      lg: 'w-5 h-5',
    };

    return (
      <div className={`w-full ${wrapperClassName}`}>
        {label && (
          <label htmlFor={selectId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {label}
          </label>
        )}
        <div className="relative group">
          {/* Gradient glow effect */}
          <div
            className={`absolute -inset-0.5 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 rounded-lg opacity-0 group-hover:opacity-20 blur transition duration-300 ${
              isFocused ? 'opacity-40' : ''
            }`}
            aria-hidden="true"
          />

          <div className="relative">
            {icon && (
              <div
                className={`absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-cyan-500 transition-colors duration-200 pointer-events-none z-10 ${iconSizeClasses[size]}`}
                aria-hidden="true"
              >
                {icon}
              </div>
            )}
            <select
              ref={ref}
              id={selectId}
              disabled={disabled}
              onChange={handleChange}
              onFocus={(e) => {
                setIsFocused(true);
                selectProps.onFocus?.(e);
              }}
              onBlur={(e) => {
                setIsFocused(false);
                selectProps.onBlur?.(e);
              }}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={
                error
                  ? `${selectId}-error`
                  : helperText
                  ? `${selectId}-helper`
                  : undefined
              }
              className={`w-full ${paddingClasses[size]} ${sizeClasses[size]} bg-white dark:bg-gray-950 border-2 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 hover:border-blue-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-muted dark:disabled:bg-gray-800 appearance-none ${
                error
                  ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500'
                  : 'border-gray-400'
              } ${className || ''}`}
              {...selectProps}
            >
              {children}
            </select>

            {/* Custom dropdown arrow */}
            <div className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none ${iconSizeClasses[size]}`} aria-hidden="true">
              <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        {error && (
          <p id={`${selectId}-error`} className="text-sm text-red-500 mt-1" role="alert">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${selectId}-helper`} className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

GradientSelect.displayName = 'GradientSelect';
